import { gql } from "@apollo/client";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
  Badge,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  IconButton,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { SOS_ACTIVE_WINDOW_HOURS } from "@caredna-marlin/utils/constants";
import { useEffect, useMemo, useRef, useState } from "react";

import SosAlertDetailContent, {
  providerName,
  SOS_STATUS_LABEL,
  sosAlertAddress,
  statusColor,
  visitAddress,
} from "~/components/command/SosAlertDetail";
import SosAlertChime from "~/components/command/SosAlertChime";
import {
  SosAlertStatusEnum,
  useAcknowledgeSosAlertMutation,
  useRecentSosAlertsQuery,
  useResolveSosAlertMutation,
} from "~/graphql/__generated__/graphql";
import { Permission, useHasPermission } from "~/hooks/permissions";
import { useSnackbar } from "~/hooks/useSnackbar";
import { useOrganization } from "~/providers/GraphqlProvider";

gql`
  query RecentSosAlerts($withinHours: Int)
  @permission(requires: manageSosAlerts)
  {
    recentSosAlerts(withinHours: $withinHours) {
      ...SosAlertDetail
    }
  }
`;

gql`
  mutation AcknowledgeSosAlert($sosAlertId: Int!)
  @permission(requires: manageSosAlerts)
  {
    acknowledgeSosAlert(sosAlertId: $sosAlertId) {
      id
      status
    }
  }
`;

gql`
  mutation ResolveSosAlert($sosAlertId: Int!, $note: String)
  @permission(requires: manageSosAlerts)
  {
    resolveSosAlert(sosAlertId: $sosAlertId, note: $note) {
      id
      status
    }
  }
`;

export default function SosAlertsIndicator() {
  const canManageSosAlerts = useHasPermission(Permission.manageSosAlerts);
  const { organizationId } = useOrganization();
  const { data, loading } = useRecentSosAlertsQuery({
    variables: { withinHours: SOS_ACTIVE_WINDOW_HOURS },
    pollInterval: 15000,
    fetchPolicy: "network-only",
    skip: !canManageSosAlerts,
    onError: (error) => console.error(error),
  });

  const [acknowledgeSosAlert, { loading: acknowledging }] = useAcknowledgeSosAlertMutation();
  const [resolveSosAlert, { loading: resolving }] = useResolveSosAlertMutation();
  const snackbar = useSnackbar();

  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [openAlertId, setOpenAlertId] = useState<number | null>(null);
  const [resolveNote, setResolveNote] = useState("");
  const seenActiveIds = useRef<Set<number>>(new Set());

  const alerts = useMemo(() => data?.recentSosAlerts ?? [], [data]);
  const activeAlertIds = useMemo(
    () => alerts
      .filter((alert) => alert.status === SosAlertStatusEnum.Active)
      .map((alert) => alert.id),
    [alerts],
  );
  const alertsKey = alerts.map((alert) => `${alert.id}:${alert.status}`).join(",");

  useEffect(() => {
    const newest = alerts.find(
      (alert) =>
        alert.status === SosAlertStatusEnum.Active && !seenActiveIds.current.has(alert.id),
    );

    alerts
      .filter((alert) => alert.status === SosAlertStatusEnum.Active)
      .forEach((alert) => seenActiveIds.current.add(alert.id));

    if (newest) {
      setOpenAlertId(newest.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alertsKey]);

  useEffect(() => {
    if (openAlertId != null && !alerts.some((alert) => alert.id === openAlertId)) {
      setOpenAlertId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alertsKey, openAlertId]);

  useEffect(() => {
    setResolveNote("");
  }, [openAlertId]);

  const selectedAlert = alerts.find((alert) => alert.id === openAlertId) ?? null;
  const hasAlerts = alerts.length > 0;

  const openAlert = (id: number) => {
    setMenuAnchor(null);
    setOpenAlertId(id);
  };

  if (!canManageSosAlerts) {
    return null;
  }

  const chime = (
    <SosAlertChime
      activeAlertIds={activeAlertIds}
      loading={loading}
      organizationId={organizationId}
    />
  );

  if (!hasAlerts) {
    return chime;
  }

  return (
    <>
      {chime}
      <Tooltip title="SOS alerts">
        <span>
          <IconButton color="error" onClick={(event) => setMenuAnchor(event.currentTarget)}>
            <Badge badgeContent={alerts.length} color="error">
              <WarningAmberIcon />
            </Badge>
          </IconButton>
        </span>
      </Tooltip>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{ paper: { sx: { width: 320 } } }}
      >
        <Typography variant="subtitle2" sx={{ px: 2, py: 1 }}>
          Active SOS alerts
        </Typography>
        <Divider />
        {alerts.map((alert) => (
          <MenuItem key={alert.id} onClick={() => openAlert(alert.id)}>
            <ListItemText
              primary={providerName(alert)}
              secondary={visitAddress(alert) ?? sosAlertAddress(alert) ?? "SOS alert"}
            />
            <Chip
              size="small"
              color={statusColor(alert.status)}
              label={SOS_STATUS_LABEL[alert.status]}
            />
          </MenuItem>
        ))}
      </Menu>

      <Dialog
        open={selectedAlert != null}
        onClose={() => setOpenAlertId(null)}
        maxWidth={false}
        slotProps={{ paper: { sx: { width: "calc(75vw + 48px)", maxWidth: "calc(75vw + 48px)" } } }}
      >
        {selectedAlert ? (
          <>
            <Box sx={{ bgcolor: "error.main", color: "error.contrastText", px: 3, py: 1.5 }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <WarningAmberIcon fontSize="small" />
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  SOS ALERT
                </Typography>
              </Stack>
            </Box>
            <DialogContent>
              <Stack spacing={1.5}>
                <SosAlertDetailContent alert={selectedAlert} />
                <TextField
                  label="Resolution note (optional)"
                  placeholder="Add context for the record before resolving…"
                  value={resolveNote}
                  onChange={(event) => setResolveNote(event.target.value)}
                  multiline
                  minRows={2}
                  fullWidth
                  size="small"
                />
              </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              {selectedAlert.status === SosAlertStatusEnum.Active ? (
                <Button
                  variant="outlined"
                  disabled={acknowledging}
                  onClick={async () => {
                    try {
                      await acknowledgeSosAlert({
                        variables: { sosAlertId: selectedAlert.id },
                        refetchQueries: ["RecentSosAlerts"],
                        awaitRefetchQueries: true,
                      });
                    } catch {
                      snackbar.setErrorMessage(
                        "Could not acknowledge the SOS alert. It may have already been updated.",
                      );
                    }
                  }}
                >
                  Acknowledge
                </Button>
              ) : null}
              <Button
                variant="contained"
                color="error"
                disabled={resolving}
                onClick={async () => {
                  const note = resolveNote.trim();
                  try {
                    await resolveSosAlert({
                      variables: { sosAlertId: selectedAlert.id, note: note || null },
                      refetchQueries: ["RecentSosAlerts"],
                      awaitRefetchQueries: true,
                    });
                    setOpenAlertId(null);
                  } catch {
                    snackbar.setErrorMessage(
                      "Could not resolve the SOS alert. It may have already been updated.",
                    );
                  }
                }}
              >
                Resolve
              </Button>
            </DialogActions>
          </>
        ) : null}
      </Dialog>
    </>
  );
}
